import { locales, type Locale } from "@/lib/i18n";

/**
 * Where a visitor goes after signing in or signing up, and the one helper that
 * builds every sign-in link (`RV-20` to `RV-22`, `docs/05` "Return paths and
 * the OAuth state").
 *
 * A return path is a page on this site under a locale (`/en/...`, `/bn/...`),
 * with its query and hash. It travels as the `next` query parameter of the
 * login, register and forgot-password pages, encoded once; `URLSearchParams`
 * decodes it once, so it is never decoded again here. Decoding it twice is what
 * sent the homepage's Google Forms import (`?importUrl=https%3A%2F%2F...`) to
 * the dashboard: the second decode produced a `://` and the old check refused
 * the whole path.
 */

/** The pages a return path never points at: signing in there again would loop. */
const AUTH_PAGES = new Set(["login", "register", "forgot-password", "reset-password", "verify-email"]);

/** A return path longer than this is refused rather than carried through a URL. */
const MAX_RETURN_PATH_LENGTH = 2048;

/** A fixed origin to resolve against; any path that leaves it is refused. */
const PARSE_ORIGIN = "https://return-path.invalid";

export type AuthPage = "login" | "register" | "forgot-password";

function isLocale(value: string | undefined): value is Locale {
  return value !== undefined && (locales as readonly string[]).includes(value);
}

function parseLocalPath(raw: string): URL | null {
  if (raw.length === 0 || raw.length > MAX_RETURN_PATH_LENGTH) return null;
  // One slash, then a path: `//host` and `/\host` are network paths to a browser.
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  // A backslash, a control character or a space: browsers rewrite or strip
  // them, so what the check sees would not be what the browser opens.
  if (/[\\\s\u0000-\u001f\u007f]/.test(raw)) return null;
  let url: URL;
  try {
    url = new URL(raw, PARSE_ORIGIN);
  } catch {
    return null;
  }
  return url.origin === PARSE_ORIGIN ? url : null;
}

/**
 * The validator. Returns the path, normalised (`/en/a/../b` is `/en/b`), when
 * it is a page on this site under a locale, and `null` otherwise. With
 * `locale`, a path under the other locale is moved to this one, so a visitor
 * who changed the language on the login page keeps the page and the language
 * they chose. An auth page is never a return path.
 */
export function safeReturnPath(raw: string | null | undefined, locale?: string): string | null {
  if (typeof raw !== "string") return null;
  const url = parseLocalPath(raw);
  if (!url) return null;
  const segments = url.pathname.split("/");
  if (!isLocale(segments[1])) return null;
  if (AUTH_PAGES.has(segments[2] ?? "")) return null;
  if (isLocale(locale)) segments[1] = locale;
  return `${segments.join("/")}${url.search}${url.hash}`;
}

/**
 * The safe return path an auth page was given in its `next` (or the older
 * `redirect` and `callbackUrl`) query parameter, or `null`.
 */
export function readReturnPath(searchParams: Pick<URLSearchParams, "get">, locale: string): string | null {
  const raw = searchParams.get("next") ?? searchParams.get("redirect") ?? searchParams.get("callbackUrl");
  return safeReturnPath(raw, locale);
}

/** Where to go after signing in: the page's return path, or the dashboard. */
export function getSafePostLoginPath(searchParams: Pick<URLSearchParams, "get">, locale: string): string {
  return readReturnPath(searchParams, locale) ?? `/${locale}/dashboard`;
}

/**
 * The return path carried by `here`: `here` itself, or, when `here` is an auth
 * page (a link drawn on the login page, say), the `next` that page carries.
 */
function returnPathFrom(here: string, locale: string): string | null {
  const url = parseLocalPath(here);
  if (!url) return null;
  const page = url.pathname.split("/")[2] ?? "";
  if (AUTH_PAGES.has(page)) return safeReturnPath(url.searchParams.get("next"), locale);
  return safeReturnPath(here, locale);
}

/**
 * The one helper behind every sign-in link: `/{locale}/login?next=<here>`.
 * `here` is the page to come back to, path plus query plus hash; `page` picks
 * the register or forgot-password page instead, which carry `next` on. Without
 * a usable `here` the link carries nothing and the login page's own default
 * (the dashboard) applies.
 */
export function signInHref(locale: string, here?: string | null, page: AuthPage = "login"): string {
  const next = here ? returnPathFrom(here, locale) : null;
  return next ? `/${locale}/${page}?next=${encodeURIComponent(next)}` : `/${locale}/${page}`;
}
